<script setup>
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

import ToastError from '@/components/ui/icons/ToastError.vue';
import ToastInfo from '@/components/ui/icons/ToastInfo.vue';
import ToastSuccess from '@/components/ui/icons/ToastSuccess.vue';
import ToastWarning from '@/components/ui/icons/ToastWarning.vue';

const props = defineProps({
  position: {
    type: String,
  },

  group: {
    type: String,
  },
});

const toast = useToast();

const closeToast = () => {
  toast.removeAllGroups();
};
</script>

<template>
  <Toast
    :position="position"
    :group="group"
  >
    <template #message="slotProps">
      <div class="toast-header">
        <h4 class="toast-title">
          <ToastSuccess v-if="slotProps.message.severity === 'success'" />
          <ToastError v-if="slotProps.message.severity === 'error'" />
          <ToastInfo v-if="slotProps.message.severity === 'info'" />
          <ToastWarning v-if="slotProps.message.severity === 'warn'" />
          <span>{{ slotProps.message.summary }}</span>
        </h4>
        <button
          class="toast-close-icon"
          type="button"
          :aria-label="$t('Close')"
          @click="closeToast"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
          >
            <path
              d="M14.1476 13L20.8913 6.26644C21.0209 6.10857 21.0871 5.90816 21.0771 5.70418C21.0671 5.5002 20.9815 5.30724 20.8371 5.16283C20.6927 5.01842 20.4998 4.93288 20.2958 4.92287C20.0918 4.91285 19.8914 4.97906 19.7335 5.10862L12.9999 11.8524L6.26634 5.10862C6.10847 4.97906 5.90806 4.91285 5.70408 4.92287C5.5001 4.93288 5.30714 5.01842 5.16273 5.16283C5.01832 5.30724 4.93279 5.5002 4.92277 5.70418C4.91275 5.90816 4.97896 6.10857 5.10853 6.26644L11.8523 13L5.10853 19.7336C4.95595 19.8877 4.87036 20.0957 4.87036 20.3125C4.87036 20.5293 4.95595 20.7374 5.10853 20.8914C5.26383 21.0416 5.4714 21.1255 5.68743 21.1255C5.90346 21.1255 6.11104 21.0416 6.26634 20.8914L12.9999 14.1477L19.7335 20.8914C19.8888 21.0416 20.0964 21.1255 20.3124 21.1255C20.5285 21.1255 20.736 21.0416 20.8913 20.8914C21.0439 20.7374 21.1295 20.5293 21.1295 20.3125C21.1295 20.0957 21.0439 19.8877 20.8913 19.7336L14.1476 13Z"
              fill="#C2C2C2"
            />
          </svg>
        </button>
      </div>
      <div class="toast-content">
        <p class="toast-description">
          {{ slotProps.message.detail }}
        </p>
      </div>
    </template>
  </Toast>
</template>

<style lang="scss">
.p-toast {
  width: auto !important;
}

.toast-content {
  width: 100%;
}

.p-toast-message-content {
  max-width: 540px;
  width: 100% !important;
}

.toast-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 4px;
}

.toast-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  color: #141414;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    display: block;
    margin-left: 14px;
    width: calc(100% - 25px);
  }

  svg {
    width: 24px;
    height: 24px;
  }
}

.toast-description {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: rgb(var(--v-theme-secondary-text-color));
  padding-left: 38px;
}

.p-toast .p-toast-message {
  border-radius: 8px;
  box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: none;

  &-content {
    padding: 16px;
    flex-wrap: wrap;
    gap: 0;
  }

  .p-toast-close-button {
    display: none;
  }

  &-success {
    border: 2px solid #a2c1a3;
    background: #ebf8ec;
  }

  &-error {
    border: 2px solid #e5aaaa;
    background: var(--Bg_color, #fbefef);
  }

  &-info {
    border: 2px solid #aac7e1;
    background: #f0f8ff;
  }

  &-warn {
    border: 2px solid #f4e386;
    background: #faf7e6;
  }

  .dark-theme & {
    &.p-toast-message {
      &-success {
        border-color: #3e5e40;
        background: #1a2e1c;
      }

      &-error {
        border-color: #7a3e3e;
        background: #2e1a1a;
      }

      &-info {
        border-color: #3e5a7a;
        background: #1a242e;
      }

      &-warn {
        border-color: #7a6e3e;
        background: #2e2a1a;
      }

      .toast-title {
        color: #fff;
      }

      .toast-description {
        color: #d1d1d1;
      }
    }
  }
}
</style>
